import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { LogOut, Menu, X, Truck, ListChecks, Settings, Building2, Plus, History, UserCircle, Users, Wrench, Repeat, Eye, EyeOff, CalendarDays, LayoutDashboard } from "lucide-react";
import { useState } from "react";

function Brand() {
  return (
    <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
      <div className="w-8 h-8 bg-[var(--lc-orange)] flex items-center justify-center rounded-sm">
        <Truck className="w-5 h-5 text-white" strokeWidth={2.5} />
      </div>
      <div className="leading-tight">
        <div className="font-bold tracking-tight text-[15px]">LC Services</div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--lc-gray)]">Livraison Cuisine</div>
      </div>
    </Link>
  );
}

const ADMIN_LINKS = [
  { to: "/admin/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-admin-dashboard" },
  { to: "/admin/requests", label: "Demandes", icon: ListChecks, testid: "nav-admin-requests" },
  { to: "/admin/agenda", label: "Agenda", icon: CalendarDays, testid: "nav-admin-agenda" },
  { to: "/admin/orders", label: "Commandes", icon: History, testid: "nav-admin-orders" },
  { to: "/admin/services", label: "Services", icon: Settings, testid: "nav-admin-services" },
  { to: "/admin/companies", label: "Entreprises", icon: Building2, testid: "nav-admin-companies" },
];

const CLIENT_LINKS = [
  { to: "/client/new", label: "Nouvelle demande", icon: Plus, testid: "nav-client-new" },
  { to: "/client/orders", label: "Mes commandes", icon: History, testid: "nav-client-orders" },
];

const COMPANY_LINKS = [
  { to: "/company/orders", label: "Commandes", icon: History, testid: "nav-company-orders" },
  { to: "/company/vendors", label: "Vendeurs", icon: Users, testid: "nav-company-vendors" },
  { to: "/company/installers", label: "Poseurs & monteurs", icon: Wrench, testid: "nav-company-installers" },
];

export default function Layout({ children }) {
  const { user, logout, exitVendor, privacy, togglePrivacy } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const isVendor = !!user?.vendor;
  const links = isAdmin ? ADMIN_LINKS : isVendor ? CLIENT_LINKS : COMPANY_LINKS;
  const displayName = isVendor ? `${user.vendor.first_name} ${user.vendor.last_name}` : user?.name;
  const roleLabel = isAdmin ? "Administrateur" : isVendor ? "Vendeur" : "Admin entreprise";

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const onSwitchProfile = () => {
    exitVendor();
    navigate("/select-profile");
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--lc-surface)]">
      <header className="bg-white border-b border-[var(--lc-border)] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Brand />

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-semibold rounded-md flex items-center gap-2 transition-colors ${
                    isActive
                      ? "bg-[var(--lc-orange-light)] text-[var(--lc-orange)]"
                      : "text-[var(--lc-dark-gray)] hover:bg-black/5"
                  }`
                }
              >
                <l.icon className="w-4 h-4" />
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link to="/profile" data-testid="profile-link" className="text-right leading-tight hover:opacity-70 transition-opacity">
              <div className="text-xs font-semibold">{displayName}</div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--lc-gray)]">{roleLabel}</div>
            </Link>
            {isVendor && (
              <button
                onClick={togglePrivacy}
                data-testid="privacy-toggle-button"
                className={`p-2 rounded-md transition-colors flex items-center gap-1.5 text-xs font-semibold ${
                  privacy ? "bg-[var(--lc-orange-light)] text-[var(--lc-orange)]" : "hover:bg-black/5 text-[var(--lc-dark-gray)]"
                }`}
                title={privacy ? "Mode confidentialité activé — prix masqués" : "Activer le mode confidentialité (masquer les prix)"}
              >
                {privacy ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            )}
            {user?.role === "client" && (
              <button
                onClick={onSwitchProfile}
                data-testid="switch-profile-button"
                className="p-2 rounded-md hover:bg-black/5 transition-colors"
                title="Changer de profil"
              >
                <Repeat className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onLogout}
              data-testid="logout-button"
              className="p-2 rounded-md hover:bg-black/5 transition-colors"
              title="Déconnexion"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-[var(--lc-border)] bg-white">
            <div className="px-4 py-2 flex flex-col">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-3 text-sm font-semibold flex items-center gap-3 border-b border-[var(--lc-border)] last:border-b-0 ${
                      isActive ? "text-[var(--lc-orange)]" : "text-[var(--lc-dark-gray)]"
                    }`
                  }
                >
                  <l.icon className="w-4 h-4" />
                  {l.label}
                </NavLink>
              ))}
              <NavLink
                to="/profile"
                data-testid="nav-profile-mobile"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 text-sm font-semibold flex items-center gap-3 border-b border-[var(--lc-border)] ${
                    isActive ? "text-[var(--lc-orange)]" : "text-[var(--lc-dark-gray)]"
                  }`
                }
              >
                <UserCircle className="w-4 h-4" />
                Mon profil
              </NavLink>
              {user?.vendor && (
                <button
                  onClick={togglePrivacy}
                  data-testid="privacy-toggle-mobile"
                  className={`px-3 py-3 text-sm font-semibold flex items-center gap-3 border-b border-[var(--lc-border)] ${
                    privacy ? "text-[var(--lc-orange)]" : "text-[var(--lc-dark-gray)]"
                  }`}
                >
                  {privacy ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  Mode confidentialité {privacy ? "activé" : "désactivé"}
                </button>
              )}
              {user?.role === "client" && (
                <button
                  onClick={() => { setOpen(false); onSwitchProfile(); }}
                  data-testid="switch-profile-mobile"
                  className="px-3 py-3 text-sm font-semibold flex items-center gap-3 border-b border-[var(--lc-border)] text-[var(--lc-dark-gray)]"
                >
                  <Repeat className="w-4 h-4" />
                  Changer de profil
                </button>
              )}
              <button
                onClick={onLogout}
                data-testid="logout-button-mobile"
                className="px-3 py-3 text-sm font-semibold flex items-center gap-3 text-red-600 border-t border-[var(--lc-border)]"
              >
                <LogOut className="w-4 h-4" />
                Déconnexion ({displayName})
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {children}
      </main>

      <footer className="border-t border-[var(--lc-border)] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 text-xs text-[var(--lc-gray)] flex items-center justify-between">
          <div>LC Services © {new Date().getFullYear()}</div>
          <div>Livraison Cuisine professionnelle</div>
        </div>
      </footer>
    </div>
  );
}
